import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/lib/utils';

export type PopoverAlign = 'start' | 'center' | 'side';

interface AnchoredPopoverProps {
  id?: string;
  anchor: DOMRect;
  width: number;
  align?: PopoverAlign;
  label: string;
  onClose: () => void;
  onEscape?: () => void;
  ignoreRef?: React.RefObject<HTMLElement>;
  className?: string;
  role?: 'dialog' | 'tooltip';
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  initialFocus?: boolean | string;
  children: React.ReactNode;
}

const MARGIN = 12;
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
const GAP = 6;
const OPEN_POPOVERS: object[] = [];

function place(anchor: DOMRect, width: number, height: number, align: PopoverAlign, rtl: boolean) {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const w = Math.min(width, viewportWidth - MARGIN * 2);
  const clampX = (x: number) => Math.max(MARGIN, Math.min(x, viewportWidth - w - MARGIN));
  const clampY = (y: number) => Math.max(MARGIN, Math.min(y, viewportHeight - height - MARGIN));
  if (align === 'side') {
    const before = rtl ? anchor.left - w - 10 : anchor.right + 10;
    const after = rtl ? anchor.right + 10 : anchor.left - w - 10;
    const fits = rtl ? before >= MARGIN : before + w <= viewportWidth - MARGIN;
    return { left: clampX(fits ? before : after), top: clampY(anchor.top - 8), width: w };
  }
  const startX = rtl ? anchor.right - w : anchor.left;
  const x = align === 'start' ? startX : anchor.left + anchor.width / 2 - w / 2;
  if (anchor.bottom + GAP + height <= viewportHeight - MARGIN) return { left: clampX(x), top: anchor.bottom + GAP, width: w };
  if (anchor.top - GAP - height >= MARGIN) return { left: clampX(x), top: anchor.top - GAP - height, width: w };
  return { left: clampX(x), top: clampY(anchor.top), width: w };
}

export const AnchoredPopover: React.FC<AnchoredPopoverProps> = ({
  id,
  anchor,
  width,
  align = 'start',
  label,
  onClose,
  onEscape,
  ignoreRef,
  className,
  role = 'dialog',
  onMouseEnter,
  onMouseLeave,
  initialFocus,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const handlers = useRef({ onClose, onEscape });
  handlers.current = { onClose, onEscape };
  const rtl = document.documentElement.dir === 'rtl';
  const [position, setPosition] = useState(() => place(anchor, width, 280, align, rtl));

  useLayoutEffect(() => {
    if (!ref.current) return;
    const next = place(anchor, width, ref.current.offsetHeight, align, rtl);
    if (next.left !== position.left || next.top !== position.top || next.width !== position.width) setPosition(next);
  });

  useLayoutEffect(() => {
    const node = ref.current;
    if (!initialFocus || !node) return;
    const trigger = document.activeElement;
    const preferred = typeof initialFocus === 'string' ? node.querySelector<HTMLElement>(initialFocus) : null;
    (preferred ?? node.querySelector<HTMLElement>(FOCUSABLE))?.focus({ preventScroll: true });
    return () => {
      const active = document.activeElement;
      const focusLeftWithPopover = !active || active === document.body || node.contains(active);
      if (focusLeftWithPopover && trigger instanceof HTMLElement && trigger.isConnected) trigger.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const me = {};
    OPEN_POPOVERS.push(me);
    const isInside = (target: EventTarget | null) =>
      target instanceof Node && (!!ref.current?.contains(target) || !!ignoreRef?.current?.contains(target));
    const isLaterPopover = (target: EventTarget | null) => {
      const popover = target instanceof Element ? target.closest('[data-planner-popover]') : null;
      return !!popover && popover !== ref.current && !!ref.current &&
        !!(ref.current.compareDocumentPosition(popover) & Node.DOCUMENT_POSITION_FOLLOWING);
    };
    const onPointer = (event: MouseEvent) => {
      if (!isInside(event.target) && !isLaterPopover(event.target)) handlers.current.onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || OPEN_POPOVERS[OPEN_POPOVERS.length - 1] !== me) return;
      event.stopPropagation();
      (handlers.current.onEscape ?? handlers.current.onClose)();
    };
    const onScroll = (event: Event) => {
      if (!isInside(event.target) && !isLaterPopover(event.target)) handlers.current.onClose();
    };
    const id = setTimeout(() => {
      document.addEventListener('mousedown', onPointer);
      document.addEventListener('keydown', onKey, true);
      document.addEventListener('scroll', onScroll, true);
    }, 0);
    return () => {
      clearTimeout(id);
      OPEN_POPOVERS.splice(OPEN_POPOVERS.indexOf(me), 1);
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [ignoreRef]);

  return createPortal(
    <div
      ref={ref}
      id={id}
      data-planner-popover
      role={role}
      aria-label={label}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ left: position.left, top: position.top, width: position.width }}
      className={cn(
        'fixed z-[80] animate-popover-in rounded-xl border border-theme-modal-border bg-theme-modal text-theme-text-primary shadow-[0_12px_32px_rgba(15,23,42,0.18)] motion-reduce:animate-none',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
};
